import { Sun, Moon, CalendarDays, Ban, ScanFace } from "lucide-react";

const SCORE_KEYS = ["pigmentation", "redness", "texture", "fine_lines", "hydration", "pores"];

function scoreTone(v) {
  if (v >= 75) return "from-emerald-500 to-emerald-400";
  if (v >= 55) return "from-amber-500 to-amber-400";
  return "from-rose-500 to-fuchsia-500";
}

export function PhotoFindings({ findings, t }) {
  if (!findings?.scores) return null;
  const s = findings.scores;
  return (
    <div className="rounded-2xl bg-white border-2 border-fuchsia-200 p-4 shadow-sm" data-testid="lumi-photo-findings">
      <div className="flex items-center justify-between gap-3 mb-3">
        <p className="text-[10px] uppercase tracking-widest text-fuchsia-700 font-bold flex items-center gap-1.5">
          <ScanFace className="w-3.5 h-3.5" /> {t.photoHeading}
        </p>
        <span className="text-xs font-bold text-slate-900 bg-fuchsia-50 border border-fuchsia-200 rounded-full px-2.5 py-0.5" data-testid="lumi-photo-overall">
          {t.overall} {s.overall}/100
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
        {SCORE_KEYS.map((k) => (
          <div key={k} className="flex items-center gap-3" data-testid={`lumi-score-${k}`}>
            <span className="w-28 text-xs text-slate-700">{t.scoreLabels[k]}</span>
            <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
              <div className={`h-full rounded-full bg-gradient-to-r ${scoreTone(s[k])}`} style={{ width: `${s[k]}%`, transition: "width 600ms ease" }} />
            </div>
            <span className="w-7 text-right text-xs font-bold text-slate-900">{s[k]}</span>
          </div>
        ))}
      </div>
      {findings.observations?.length > 0 && (
        <ul className="mt-3 space-y-1">
          {findings.observations.map((o, i) => (
            <li key={i} className="text-xs sm:text-sm text-slate-700 leading-snug flex gap-2"><span className="text-fuchsia-500">•</span>{o}</li>
          ))}
        </ul>
      )}
      {findings.photo_quality_note && (
        <p className="mt-2 text-[11px] text-slate-500 italic">{findings.photo_quality_note}</p>
      )}
    </div>
  );
}

function Steps({ icon: Icon, title, items, tone }) {
  if (!items?.length) return null;
  return (
    <div className={`rounded-xl border p-3 ${tone}`}>
      <p className="text-[10px] uppercase tracking-widest font-extrabold flex items-center gap-1.5 mb-2"><Icon className="w-3.5 h-3.5" /> {title}</p>
      <ol className="space-y-1.5">
        {items.map((s, i) => (
          <li key={i} className="text-xs sm:text-sm text-slate-800 leading-snug flex gap-2">
            <span className="w-5 h-5 rounded-full bg-white/80 border border-current text-[10px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>
            <span>{s}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

export function RoutinePlan({ plan, t }) {
  if (!plan) return null;
  return (
    <div className="rounded-2xl bg-white border-2 border-fuchsia-200 p-4 shadow-sm space-y-3" data-testid="lumi-routine-plan">
      <p className="text-[10px] uppercase tracking-widest text-fuchsia-700 font-bold flex items-center gap-1.5">
        <CalendarDays className="w-3.5 h-3.5" /> {t.planHeading}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Steps icon={Sun} title={t.am} items={plan.am} tone="bg-amber-50 border-amber-200 text-amber-800" />
        <Steps icon={Moon} title={t.pm} items={plan.pm} tone="bg-indigo-50 border-indigo-200 text-indigo-800" />
      </div>
      {plan.actives_schedule && (
        <p className="text-xs sm:text-sm text-slate-800 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2" data-testid="lumi-actives-schedule">
          <span className="font-bold">{t.schedule}:</span> {plan.actives_schedule}
        </p>
      )}
      {plan.weeks?.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {plan.weeks.map((w, i) => (
            <div key={i} className="rounded-xl border border-fuchsia-100 bg-gradient-to-br from-fuchsia-50/60 to-white p-3" data-testid={`lumi-week-${i + 1}`}>
              <p className="text-[11px] font-extrabold text-fuchsia-700 uppercase tracking-wider">{w.week}</p>
              <p className="text-xs text-slate-800 mt-1"><span className="font-bold">{t.do}:</span> {w.do}</p>
              <p className="text-xs text-slate-600 mt-1"><span className="font-bold">{t.expect}:</span> {w.expect}</p>
            </div>
          ))}
        </div>
      )}
      {plan.avoid_together?.length > 0 && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-3" data-testid="lumi-avoid-together">
          <p className="text-[10px] uppercase tracking-widest font-extrabold text-rose-700 flex items-center gap-1.5 mb-1"><Ban className="w-3.5 h-3.5" /> {t.avoid}</p>
          <ul className="space-y-1">
            {plan.avoid_together.map((a, i) => <li key={i} className="text-xs sm:text-sm text-rose-900 leading-snug">• {a}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}
